import saveAs from 'file-saver';

import { UploadYieldMapParams } from '../hooks/use-upload-yield-map/upload-yield-map';
import { NewsResponse } from '../types';
import { AdminStatsResponse, CampaignStatsResponse } from '../types/stats';
import { SynthesisResponse } from '../types/synthesis';

import { FORM_STATUS } from '@advices/data-access';
import {
  Filters,
  formatFilters,
  handleRequest,
  MultisectActionDto,
  privateClient,
} from '@data-access';

export const dashboardApi = {
  news: async () => {
    return handleRequest<NewsResponse>(privateClient.get('news/').json());
  },
  getCampaignStats: async () => {
    const data = await handleRequest<CampaignStatsResponse>(
      privateClient.get('dashboard/campaign/').json(),
    );

    const {
      wheat: wheatData,
      barley: barleyData,
      rapeseed: rapeseedData,
    } = data;
    const {
      real_mean: wheatRealMean,
      olympic_mean: wheatOlympicMean,
      ...wheatStats
    } = wheatData;
    const {
      real_mean: barleyRealMean,
      olympic_mean: barleyOlympicMean,
      ...barleyStats
    } = barleyData;
    const {
      real_mean: rapeseedRealMean,
      olympic_mean: rapeseedOlympicMean,
      ...rapeseedStats
    } = rapeseedData;
    const campaignStats = {
      wheat: {
        real_mean: wheatRealMean,
        olympic_mean: wheatOlympicMean,
        stats: Object.entries(wheatStats).map(([key, value]) => ({
          name: key,
          ...value,
        })),
      },
      barley: {
        real_mean: barleyRealMean,
        olympic_mean: barleyOlympicMean,
        stats: Object.entries(barleyStats).map(([key, value]) => ({
          name: key,
          ...value,
        })),
      },
      rapeseed: {
        real_mean: rapeseedRealMean,
        olympic_mean: rapeseedOlympicMean,
        stats: Object.entries(rapeseedStats).map(([key, value]) => ({
          name: key,
          ...value,
        })),
      },
    };
    return campaignStats;
  },
  getAdminStats: async (filters: Filters) => {
    return handleRequest<AdminStatsResponse>(
      privateClient
        .get('dashboard/admin/', {
          searchParams: {
            ...formatFilters(filters),
          },
        })
        .json(),
    );
  },
  exportStats: async () => {
    const res = await handleRequest(
      privateClient.get('dashboard/admin/export/'),
    );

    // Extract filename from header
    const filename = res.headers
      .get('content-disposition')
      ?.split(';')
      .find((n) => n.includes('filename='))
      ?.replace('filename=', '')
      .trim();

    const blob = await res.blob();

    saveAs(blob, filename);

    return res;
  },

  getSynthesis: async (filters: Filters) => {
    const response = await handleRequest<SynthesisResponse>(
      privateClient
        .get('synthesis/', {
          searchParams: {
            ...formatFilters(filters),
          },
        })
        .json(),
    );

    const species = response.results.features.map((feature) => {
      return feature.properties.species;
    });
    const uniqueSpecies = Array.from(new Set(species.map((s) => s.id)))
      .map((id) => species.find((s) => s.id === id))
      .filter((s) => s !== undefined);

    return {
      ...response,
      options: {
        species: uniqueSpecies.map((s) => ({
          value: s.id.toString(),
          label: s.name,
        })),
      },
      results: response.results.features.map((feature) => ({
        ...feature.properties,
        id: feature.id as number,
      })),
    };
  },
  updateSynthesis: async (data: {
    ids: number[];
    real_yield?: number;
    species?: string;
  }) => {
    return handleRequest<{
      status: string;
    }>(
      privateClient
        .patch('synthesis/many/', {
          json: data,
        })
        .json(),
    );
  },

  exportSynthesis: async (data: MultisectActionDto) => {
    const res = await handleRequest<{
      id: number;
      status: FORM_STATUS;
      instance_id: number;
    }>(
      privateClient
        .post(`synthesis/prep_synthesis/`, {
          json: data,
        })
        .json(),
    );

    return res;
  },
  downloadExport: async (opts: { id: number }) => {
    const res = await handleRequest(
      privateClient.post(`synthesis/synthesis/`, {
        json: {
          instance_id: opts.id,
        },
      }),
    );

    // Extract filename from header
    const filename = res.headers
      .get('content-disposition')
      ?.split(';')
      .find((n) => n.includes('filename='))
      ?.replace('filename=', '')
      .trim();

    const blob = await res.blob();

    saveAs(blob, filename);

    return res;
  },

  uploadYieldMap: ({
    file,
    farm,
    yield_float,
    comment,
  }: UploadYieldMapParams) => {
    const data = new FormData();
    data.append('yield_float', yield_float.toString());
    data.append('farm', farm.toString());
    data.append('comment', comment);
    data.append('file', file);

    return privateClient
      .post('upload_yield_map/', {
        body: data,
      })
      .json<{ status: string }>();
  },
};
